import axios from "axios";
import { API_BASE, PLATFORM_API_BASE, API_AUTH_FEISHU } from "@/config";

const api = axios.create({
  baseURL: API_BASE,
  headers: {
    "Content-Type": "application/json",
  },
});

// Inject the access_token (set by the Passgate /callback/login redirect into
// localStorage) as a Bearer Authorization header on every request.
//
// Why this is the fix for the "Sign in 一闪而过" symptom: the backend
// `get_principal` dependency authenticates either via Authorization Bearer
// or via the `passgate_token` cookie. Until this interceptor existed, the
// SPA stored the token in localStorage but **never sent it** — so /me
// fell through to cookie-only auth. Cookies are racy (set during the
// OAuth callback flow, sometimes still in flight when the dashboard's
// first /me hits) and cookie-only also means private-mode browsers
// without 3rd-party cookies see a perpetual 401. With Bearer in play,
// /me succeeds whenever localStorage holds a valid token, and the
// sidebar's "Sign in" link only appears when there really is no auth.
//
// We do NOT clear the token on 401 here — leave that to the caller.
// Some 401s are recoverable (race; refresh token flow; re-auth in
// another tab) and we don't want a transient blip to log the user out.
api.interceptors.request.use((config) => {
  if (typeof window !== "undefined") {
    const token = window.localStorage.getItem("access_token");
    if (token && !config.headers?.Authorization) {
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

export default api;

// Software identity is read-only on the SPA — the canonical list lives in
// Passgate. ``list`` returns the local mirror (fast); ``refresh`` asks
// the backend to re-sync from Passgate and returns the fresh list.
// There is no `create` / `delete` — operators register apps in Passgate
// and use ``refresh`` to surface them here. ``retire`` / ``unretire`` are
// the one mutable bit (lifecycle_state), gated owner-only server-side.
export const softwareApi = {
  list: () => api.get("/software"),
  refresh: () => api.post("/software/refresh"),
  get: (id: string) => api.get(`/software/${id}`),
  // Owner-only on the backend (same tier as promote→stable). A 403 comes
  // back with the same `elevate_hint` shape promote uses, so the UI can
  // point the operator at `brood auth elevate <appid> --role owner`.
  retire: (id: string, reason: string) =>
    api.post(`/software/${encodeURIComponent(id)}/retire`, { reason }),
  unretire: (id: string) =>
    api.post(`/software/${encodeURIComponent(id)}/unretire`, {}),
};

export const channelsApi = {
  list: () => api.get("/channels"),
  getVersions: (channel: string) => api.get(`/channels/${channel}/versions`),
};

// Default list cap used by views that haven't been migrated to the
// manifest API yet. The remaining list-style endpoint (/channels/{n}/versions)
// already clamps server-side; this constant survives as a convenience.
export const LIST_DEFAULT_LIMIT = 200;

// NOTE: legacy releasesApi / promotionsApi / buildsApi were removed in the
// 2026-05-21 v1↔v2 API merge. The manifest-shaped replacement is mounted
// under /api/v1/releases/{software}. `history` returns full Manifest rows
// (artifacts/provenance/depends_on included); `promote` + `register` are
// both wired in the SPA (register via RegisterReleaseView); `audit` is the
// per-(software,version) register+promote event trail.
export const releasesApi = {
  history: (
    softwareId: string,
    opts: { channel?: string; limit?: number } = {},
  ) =>
    api.get(`/releases/${encodeURIComponent(softwareId)}/history`, {
      params: {
        ...(opts.channel ? { channel: opts.channel } : {}),
        ...(opts.limit ? { limit: opts.limit } : {}),
      },
    }),
  promote: (
    softwareId: string,
    payload: {
      from_channel: string;
      to_channel: string;
      version?: string;
      reason?: string;
    },
  ) =>
    api.post(
      `/releases/${encodeURIComponent(softwareId)}/promote`,
      payload,
    ),
  register: (softwareId: string, payload: unknown) =>
    api.post(
      `/releases/${encodeURIComponent(softwareId)}/register`,
      payload,
    ),
  // Chronological register + promote events for one (software, version).
  // Read-only, no permission filter (matches the manifest read).
  audit: (softwareId: string, version: string) =>
    api.get(
      `/releases/${encodeURIComponent(softwareId)}/${encodeURIComponent(version)}/audit`,
    ),
};

export const dashboardApi = {
  get: () => api.get("/dashboard"),
  getCycles: (softwareId: string, days = 90) =>
    api.get("/dashboard/cycles", {
      params: { software_id: softwareId, days },
    }),
};

// ReleaseTrain — the company-level canary cadence (daily cron + current
// major X) and per-software participation bindings. `tick` fires the
// daily bump on demand; `newCycle` rolls X→X+1. NOTE: these endpoints
// carry no server-side role gate today (unlike promote/retire), so the
// UI does not pre-filter by role — writes succeed for any signed-in user.
export const trainsApi = {
  list: () => api.get("/trains"),
  get: (id: number) => api.get(`/trains/${id}`),
  create: (payload: {
    name: string;
    daily_cron?: string;
    timezone?: string;
    current_major?: number;
    anchor_date: string;
    enabled?: boolean;
  }) => api.post("/trains", payload),
  update: (
    id: number,
    payload: { daily_cron?: string; timezone?: string; enabled?: boolean },
  ) => api.patch(`/trains/${id}`, payload),
  newCycle: (id: number) => api.post(`/trains/${id}/new-cycle`, {}),
  tick: (id: number) => api.post(`/trains/${id}/tick`, {}),
  listBindings: (id: number) => api.get(`/trains/${id}/bindings`),
  upsertBinding: (payload: {
    software_id: string;
    train_id: number;
    participation: string;
  }) => api.put("/trains/bindings", payload),
};

// Cross-service version metadata is mounted at /api/version (not under
// /api/v1/* — it is a platform-wide contract, not a release-channel
// versioned API). Override baseURL for this one call.
export const versionApi = {
  get: () => api.get("/version", { baseURL: PLATFORM_API_BASE }),
};

// Current-user introspection. 401 / 500 are expected when unauthenticated
// — callers should handle that and render a "Sign in" affordance instead
// of surfacing the error.
export const meApi = {
  get: () => api.get("/me"),
};

// Passgate (飞书 OAuth) login flow. ``getLoginUrl`` returns
//   { login_url: "https://passgate.brood.neolix.net/api/auth/feishu?origin_url=..." }
// the SPA navigates to that URL; Passgate redirects back to the
// configured ``origin_url`` with ``#access_token=XXX`` in the hash,
// which the router stashes into localStorage on next mount.
export const authApi = {
  getLoginUrl: (originUrl?: string) =>
    api.get(API_AUTH_FEISHU, {
      baseURL: "",
      params: originUrl ? { origin_url: originUrl } : undefined,
    }),
  // RFC-0128: 登出改为整页导航到同源后端 ``/logout``(AppLayout.signOut)——
  // 后端 broodbridge.install_logout_route 可靠删三类 cookie 并 302 到 /login。
  // 旧的 ``logout``(POST /api/auth/logout)与 ``getConfig``(跨域撤 passport 会话)
  // 已随之移除:跨域 fetch 清父域 HttpOnly cookie 本就不可靠,正是被静默登回的根因。
};
